import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../../_helper/Auth";
import { useRefreshToken } from "./useRefreshToken";
import { saveRedirectPath } from "../redirectStore";
import {
  readAccessTokenExpiryMs,
  persistAccessTokenExpiry,
  REFRESH_BEFORE_EXPIRY_MS,
} from "../tokenExpiry";
import {
  readPasswordChangeRequiredFromPayload,
  readEmailVerificationFromPayload,
} from "../authPayloadGates";
import { MANDATORY_CHANGE_PASSWORD_PATH } from "../../Routes/PrivateRoute";

export function useProactiveAccessTokenRefresh() {
  const {
    token,
    addToken,
    removeToken,
    removeUser,
    setEmailVerificationPending,
    setPasswordChangeRequired,
  } = useAuthContext();
  const refreshTokenApi = useRefreshToken();
  const navigate = useNavigate();
  const location = useLocation();

  const refreshTimerRef = useRef(null);

  /**
   * Mutable refs so that navigate / location.pathname are never in the
   * refresh-timer effect's dependency array — navigation must NOT reset
   * the refresh countdown.
   */
  const navigateRef = useRef(navigate);
  const locationRef = useRef({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
  });

  /** Always-fresh logout so the refresh timer does not capture stale closures. */
  const logoutRef = useRef(null);
  logoutRef.current = () => {
    const loc = locationRef.current;
    saveRedirectPath(loc.pathname, loc.search, loc.hash);
    removeToken();
    removeUser();
    navigateRef.current("/login", {
      replace: true,
      state: { from: loc },
    });
  };

  const isAuthenticated = !!(
    token?.access_token &&
    token.access_token !== "null" &&
    token.access_token !== "undefined"
  );

  /* ─────────────────────────────────────────────────────────────────────────
     Proactive refresh
     Only re-runs when the access_token itself changes (login / successful
     refresh). Navigation does NOT reset this timer.
  ───────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const clearRefreshTimer = () => {
      if (refreshTimerRef.current != null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };

    if (!isAuthenticated) {
      clearRefreshTimer();
      return;
    }

    const at = token?.access_token;
    let expiryMs = readAccessTokenExpiryMs(at);
    if (!expiryMs && at) {
      expiryMs = persistAccessTokenExpiry(at);
    }
    if (!expiryMs) {
      clearRefreshTimer();
      return;
    }

    // Refresh REFRESH_BEFORE_EXPIRY_MS before expiry; if we're already inside
    // that window (or past JWT exp but session still valid), refresh now.
    const delay = Math.max(
      expiryMs - REFRESH_BEFORE_EXPIRY_MS - Date.now(),
      0
    );

    const runRefresh = async () => {
      const storedAt = localStorage.getItem("access_token");
      const storedRt = localStorage.getItem("refresh_token");
      const validAt =
        storedAt && storedAt !== "null" && storedAt !== "undefined"
          ? storedAt
          : null;
      const validRt =
        storedRt && storedRt !== "null" && storedRt !== "undefined"
          ? storedRt
          : null;

      // No usable access_token in storage — we cannot call /refresh at all.
      // This can only happen if the user (or another tab) cleared the session,
      // so logging out is the right thing here.
      if (!validAt) {
        console.warn("[auth] no access_token in storage — logging out.");
        logoutRef.current?.();
        return;
      }

      if (!validRt) {
        console.warn(
          "[auth] no refresh_token in storage — calling /refresh with access_token only."
        );
      }

      try {
        const newTokens = await refreshTokenApi({
          access_token: validAt,
          refresh_token: validRt,
        });

        // Defensive: normalizeRefreshResponse already guarantees access_token,
        // but if anything slipped through, treat it as a transient failure and
        // KEEP the existing tokens. Do NOT log out.
        if (!newTokens?.access_token) {
          console.warn(
            "[auth] refresh returned no access_token — keeping current session, will retry on next cycle."
          );
          return;
        }

        // Persist new access_token + expires_in, reuse old refresh_token if
        // the server didn't issue a new one. The next refresh cycle will use
        // these new values automatically.
        addToken({
          access_token: newTokens.access_token,
          refresh_token: newTokens.refresh_token ?? validRt,
          expires_in: newTokens.expires_in,
        });

        const emailPending = readEmailVerificationFromPayload(newTokens);
        if (emailPending !== undefined) {
          setEmailVerificationPending(emailPending);
        }
        const pwdRequired = readPasswordChangeRequiredFromPayload(newTokens);
        if (pwdRequired !== undefined) {
          setPasswordChangeRequired(pwdRequired);
        }
        if (pwdRequired === true) {
          if (locationRef.current?.pathname !== MANDATORY_CHANGE_PASSWORD_PATH) {
            navigateRef.current(MANDATORY_CHANGE_PASSWORD_PATH, { replace: true });
          }
        } else if (
          emailPending === true &&
          locationRef.current?.pathname !== "/verify-email"
        ) {
          navigateRef.current("/verify-email", { replace: true });
        }
      } catch (err) {
        // Only the /refresh endpoint rejecting our credentials with 401 (or
        // 403) ends the session. Everything else — network errors, 500, CORS,
        // timeouts, malformed bodies — is treated as transient: keep the user
        // logged in with the existing tokens; the next axios 401 will retry.
        const status = err?.response?.status;
        const isAuthFailure = status === 401 || status === 403;
        if (isAuthFailure) {
          console.warn(
            `[auth] refresh rejected by server (status ${status}) → logging out.`
          );
          logoutRef.current?.();
        } else {
          console.warn(
            "[auth] refresh failed transiently — keeping session:",
            err?.message ?? status ?? err
          );
        }
      }
    };

    clearRefreshTimer();
    refreshTimerRef.current = setTimeout(runRefresh, delay);

    return clearRefreshTimer;
  }, [
    isAuthenticated,
    token?.access_token,
    token?.refresh_token,
    addToken,
    refreshTokenApi,
    setEmailVerificationPending,
    setPasswordChangeRequired,
  ]);
}
